import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Shield, AlertTriangle } from 'lucide-react';
import { siteConfig } from '../data/mock';

const SafeguardingPage = () => {
  return (
    <main>
      <div className="page-header">
        <div className="container">
          <div className="breadcrumb">
            <Link to="/">Home</Link>
            <ChevronRight size={16} />
            <span>Safeguarding</span>
          </div>
          <h1 className="display-medium">Safeguarding Policy</h1>
          <p className="body-large">Our commitment to the safety of children, vulnerable adults, and animals.</p>
        </div>
      </div>

      <section className="section">
        <div className="container" style={{ maxWidth: '800px' }}>
          <div className="network-card" style={{ padding: '40px' }}>
            <p className="body-small" style={{ marginBottom: '32px' }}>Last updated: August 2025</p>

            <div 
              style={{ 
                background: 'var(--brand-primary)', 
                padding: '24px', 
                borderRadius: '16px',
                marginBottom: '32px',
                display: 'flex',
                gap: '16px'
              }}
            >
              <Shield size={32} color="var(--brand-dark)" style={{ flexShrink: 0 }} />
              <div>
                <h3 className="heading-3" style={{ marginBottom: '8px' }}>Our Commitment</h3>
                <p className="body-medium">
                  The Grazing Trust is committed to safeguarding and promoting the welfare of children, vulnerable adults, and animals. We expect all staff, trustees, volunteers, and visitors to share this commitment.
                </p>
              </div>
            </div>

            <h2 className="heading-2" style={{ marginBottom: '16px' }}>1. Purpose</h2>
            <p className="body-medium" style={{ marginBottom: '24px' }}>
              This policy sets out our commitment to safeguarding and the procedures we have in place to protect vulnerable individuals and animals in connection with our work.
            </p>

            <h2 className="heading-2" style={{ marginBottom: '16px' }}>2. Scope</h2>
            <p className="body-medium" style={{ marginBottom: '24px' }}>
              This policy applies to all trustees, staff, volunteers, contractors, and anyone else working on behalf of The Grazing Trust.
            </p>

            <h2 className="heading-2" style={{ marginBottom: '16px' }}>3. Safeguarding Children and Vulnerable Adults</h2>
            <p className="body-medium" style={{ marginBottom: '12px' }}>We are committed to:</p>
            <ul style={{ paddingLeft: '20px', marginBottom: '24px' }}>
              <li className="body-medium" style={{ marginBottom: '8px' }}>Creating a safe environment for all visitors to our premises</li>
              <li className="body-medium" style={{ marginBottom: '8px' }}>Ensuring appropriate supervision at all times</li>
              <li className="body-medium" style={{ marginBottom: '8px' }}>Conducting appropriate checks (including PVG disclosure) for roles involving contact with children or vulnerable adults</li>
              <li className="body-medium" style={{ marginBottom: '8px' }}>Training staff and volunteers to recognise and respond to safeguarding concerns</li>
              <li className="body-medium">Maintaining clear reporting procedures</li>
            </ul>

            <h2 className="heading-2" style={{ marginBottom: '16px' }}>4. Animal Welfare</h2>
            <p className="body-medium" style={{ marginBottom: '12px' }}>Our commitment to animal welfare includes:</p>
            <ul style={{ paddingLeft: '20px', marginBottom: '24px' }}>
              <li className="body-medium" style={{ marginBottom: '8px' }}>Providing the highest standards of care for all horses in our sanctuary</li>
              <li className="body-medium" style={{ marginBottom: '8px' }}>Regular veterinary oversight and health monitoring</li>
              <li className="body-medium" style={{ marginBottom: '8px' }}>Thorough vetting of all rehoming placements</li>
              <li className="body-medium" style={{ marginBottom: '8px' }}>Responding promptly to welfare concerns reported to us</li>
              <li className="body-medium">Working with appropriate authorities on welfare matters</li>
            </ul>

            <h2 className="heading-2" style={{ marginBottom: '16px' }}>5. Recruitment and Training</h2>
            <p className="body-medium" style={{ marginBottom: '24px' }}>
              All trustees, staff, and volunteers undergo appropriate recruitment checks and induction. Those in roles involving contact with children, vulnerable adults, or direct animal care receive additional safeguarding training.
            </p>

            <h2 className="heading-2" style={{ marginBottom: '16px' }}>6. Reporting Concerns</h2>
            
            <div 
              style={{ 
                background: '#FEF3C7', 
                padding: '20px', 
                borderRadius: '12px',
                marginBottom: '24px',
                display: 'flex',
                gap: '12px'
              }}
            >
              <AlertTriangle size={24} color="#92400E" style={{ flexShrink: 0 }} />
              <div>
                <p className="body-medium" style={{ color: '#92400E', fontWeight: 600, marginBottom: '8px' }}>If you have an immediate safeguarding concern:</p>
                <p className="body-small" style={{ color: '#92400E' }}>
                  • For children/vulnerable adults: Contact Police Scotland (101 or 999 in emergency) or local Social Work<br />
                  • For animal welfare emergencies: Contact SSPCA (03000 999 999) or call 999
                </p>
              </div>
            </div>

            <p className="body-medium" style={{ marginBottom: '24px' }}>
              For non-emergency concerns related to The Grazing Trust&apos;s activities, please contact our Designated Safeguarding Lead at {siteConfig.email}.
            </p>

            <h2 className="heading-2" style={{ marginBottom: '16px' }}>7. Confidentiality</h2>
            <p className="body-medium" style={{ marginBottom: '24px' }}>
              All safeguarding concerns are treated confidentially and shared only with those who need to know. However, we may need to share information with statutory agencies where there is a risk of harm.
            </p>

            <h2 className="heading-2" style={{ marginBottom: '16px' }}>8. Review</h2>
            <p className="body-medium" style={{ marginBottom: '24px' }}>
              This policy is reviewed annually by the Board of Trustees to ensure it remains fit for purpose and reflects current legislation and best practice.
            </p>

            <h2 className="heading-2" style={{ marginBottom: '16px' }}>9. Contact</h2>
            <p className="body-medium">
              For questions about this policy, contact:<br />
              <strong>{siteConfig.name}</strong><br />
              Email: {siteConfig.email}
            </p>
          </div>
        </div>
      </section>
    </main>
  );
};

export default SafeguardingPage;
